import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { jwtDecode } from 'jwt-decode';
import type { User, AuthState } from '@/types';
import api from '@/services/api';

const IDLE_TIMEOUT = 30 * 60 * 1000;
const IDLE_WARNING = 25 * 60 * 1000;

interface JwtPayload {
  sub: number;
  email: string;
  user_type: string;
  allowed_portals?: string[];
  exp: number;
}

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    token: localStorage.getItem('auth_token'),
    isAuthenticated: false,
    isLoading: true,
  });

  const refreshUser = useCallback(async () => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      setState({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      });
      return;
    }

    try {
      // Decode and validate JWT before making API call
      const decoded = jwtDecode<JwtPayload>(token);

      // Check if token is expired
      if (decoded.exp * 1000 < Date.now()) {
        localStorage.removeItem('auth_token');
        setState({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
        });
        return;
      }

      // BLOCK employees - they should use Factory/Sales/Shipping/Workstation portals
      if (decoded.user_type === 'employee') {
        localStorage.removeItem('auth_token');
        setState({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
        });
        return;
      }

      // Only allow dealer_contact and dealer_sales_rep
      const allowedUserTypes = ['dealer_contact', 'dealer_sales_rep'];
      if (!allowedUserTypes.includes(decoded.user_type)) {
        localStorage.removeItem('auth_token');
        setState({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
        });
        return;
      }

      const user = await api.getProfile();
      setState({
        user,
        token,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch (error) {
      localStorage.removeItem('auth_token');
      setState({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      });
    }
  }, []);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const login = async (email: string, password: string) => {
    const { user: loginUser, accessToken: token } = await api.login(email, password);

    // BLOCK employees - they should use Factory/Sales/Shipping/Workstation portals
    if (loginUser.user_type === 'employee') {
      throw new Error('Employee accounts cannot access the Dealer Portal. Please use the appropriate portal for your role (Factory Admin, Sales, Shipping, or Workstation).');
    }

    // Validate portal access - only dealer_contact and dealer_sales_rep allowed
    const allowedUserTypes = ['dealer_contact', 'dealer_sales_rep'];
    if (!allowedUserTypes.includes(loginUser.user_type)) {
      throw new Error('This account does not have dealer portal access.');
    }

    // Also check allowed_portals array if present
    if (loginUser.allowed_portals && !loginUser.allowed_portals.includes('dealer')) {
      throw new Error('This account does not have dealer portal access.');
    }

    localStorage.setItem('auth_token', token);

    // Fetch full profile with dealer info after login
    try {
      const fullProfile = await api.getProfile();
      setState({
        user: { ...loginUser, ...fullProfile },
        token,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch {
      // Fallback to login user data
      setState({
        user: loginUser,
        token,
        isAuthenticated: true,
        isLoading: false,
      });
    }
  };

  const logout = useCallback(() => {
    localStorage.removeItem('auth_token');
    setState({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
    });
  }, []);

  const idleTimer = useRef<ReturnType<typeof setTimeout>>();
  const warningTimer = useRef<ReturnType<typeof setTimeout>>();
  const [showIdleWarning, setShowIdleWarning] = useState(false);

  useEffect(() => {
    if (!state.isAuthenticated) return;
    const resetIdle = () => {
      setShowIdleWarning(false);
      clearTimeout(idleTimer.current);
      clearTimeout(warningTimer.current);
      warningTimer.current = setTimeout(() => setShowIdleWarning(true), IDLE_WARNING);
      idleTimer.current = setTimeout(() => { logout(); window.location.href = '/login'; }, IDLE_TIMEOUT);
    };
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];
    events.forEach(e => window.addEventListener(e, resetIdle, { passive: true }));
    resetIdle();
    return () => { events.forEach(e => window.removeEventListener(e, resetIdle)); clearTimeout(idleTimer.current); clearTimeout(warningTimer.current); };
  }, [state.isAuthenticated, logout]);

  return (
    <AuthContext.Provider value={{ ...state, login, logout, refreshUser }}>
      {children}
      {showIdleWarning && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'white', borderRadius: 12, padding: 24, maxWidth: 360, textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>&#9201;</div>
            <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Session Expiring</h3>
            <p style={{ color: '#666', fontSize: 14, marginBottom: 16 }}>You'll be signed out in 5 minutes due to inactivity. Move your mouse or press any key to stay signed in.</p>
            <button onClick={() => setShowIdleWarning(false)} style={{ padding: '8px 24px', background: '#2563eb', color: 'white', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}>Stay Signed In</button>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
